import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IconCheck, IconGripVertical, IconEdit, IconTrash, IconPlus, IconX, IconUpload, IconSearch, IconTicket } from '@tabler/icons-react';
import { 
  getMarketingPage, 
  createMarketingPage, 
  updateMarketingPage, 
  uploadBase64Image,
  getImageUrl,
  fetchPublishedVouchers,
  fetchCampaigns,
  fetchCampaignVouchers
} from '../services/api';
import { isMarketingOpsVoucherRowDisplayable, isVoucherListItemDisplayable } from '../utils/voucherExpiry';
import './CreateMarketingPage.css';

/**
 * Convert image URL to base64 data URL
 * Required for cropping images loaded from server (CORS workaround)
 */
const urlToBase64 = async (url) => {
  if (!url) return null;
  if (url.startsWith('data:')) return url; // Already base64
  
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting URL to base64:', error);
    return null;
  }
};

// Mock voucher data
// Vouchers will be fetched from API

const VOUCHER_MODAL_PAGE_SIZE = 10;

const STEPS = [
  { id: 1, label: 'Basic Setting' },
  { id: 2, label: 'Banner Details' },
  { id: 3, label: 'Content Configuration' },
  { id: 4, label: 'Confirmation of creation' }
];

function CreateMarketingPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slogan: '',
    companyLogo: null, // Cropped image
    companyLogoName: '',
    originalCompanyLogo: null, // Original uncropped image for re-cropping
    savedCompanyLogoCrop: null // Store crop settings for re-cropping
  });
  const [contentConfig, setContentConfig] = useState({
    linkVoucherEnabled: false,
    linkVoucherTitle: '',
    vouchers: [],
    linkCampaignEnabled: false,
    linkCampaignTitle: '',
    selectedCampaign: ''
  });
  const [banners, setBanners] = useState([]);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    image: null,
    imageName: '',
    originalImageUrl: null, // Store original image URL for re-cropping
    savedCrop: null, // Store crop position for re-cropping
    savedZoom: 1, // Store zoom for re-cropping
    linkType: 'voucher', // 'voucher' or 'custom'
    selectedVoucher: '',
    selectedVoucherData: null,
    link: ''
  });
  const [bannerLinkError, setBannerLinkError] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteVoucherConfirmId, setDeleteVoucherConfirmId] = useState(null);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherSearch, setVoucherSearch] = useState('');
  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [showBannerVoucherModal, setShowBannerVoucherModal] = useState(false); // Modal for banner voucher selection
  const [bannerVoucherSearch, setBannerVoucherSearch] = useState('');
  const [tempSelectedVoucher, setTempSelectedVoucher] = useState(''); // Temporary selection in modal
  // Banner voucher modal filters
  const [bannerVoucherFilters, setBannerVoucherFilters] = useState({
    brand: '',
    category: '',
    voucherType: '',
    country: ''
  });
  const [contentVoucherFilters, setContentVoucherFilters] = useState({
    brand: '',
    category: '',
    voucherType: '',
    country: ''
  });
  const [contentVoucherSearch, setContentVoucherSearch] = useState('');
  // API Vouchers state
  const [apiVouchers, setApiVouchers] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [voucherPagination, setVoucherPagination] = useState({
    page: 1,
    size: VOUCHER_MODAL_PAGE_SIZE,
    totalCount: 0,
    lastPage: 1
  });
  // API Campaigns state
  const [apiCampaigns, setApiCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  
  // Campaign vouchers modal state
  const [showCampaignVouchersModal, setShowCampaignVouchersModal] = useState(false);
  const [campaignVouchers, setCampaignVouchers] = useState([]);
  const [campaignVouchersLoading, setCampaignVouchersLoading] = useState(false);
  const [selectedCampaignForVouchers, setSelectedCampaignForVouchers] = useState(null);
  // Filtered voucher count for selected campaign (fetched from API with filters)
  const [filteredVoucherCount, setFilteredVoucherCount] = useState({});
  
  const fileInputRef = useRef(null);
  const companyLogoInputRef = useRef(null);
  const bannerImageObjectUrlRef = useRef(null);
  const companyLogoObjectUrlRef = useRef(null);
  
  // Create object URL for banner image thumbnail (clean up on unmount or change)
  const bannerImageThumbnail = useMemo(() => {
    // Clean up previous object URL
    if (bannerImageObjectUrlRef.current) {
      URL.revokeObjectURL(bannerImageObjectUrlRef.current);
      bannerImageObjectUrlRef.current = null;
    }
    
    if (bannerForm.image) {
      // New file uploaded - create object URL
      const objectUrl = URL.createObjectURL(bannerForm.image);
      bannerImageObjectUrlRef.current = objectUrl;
      return objectUrl;
    }
    return null;
  }, [bannerForm.image]);
  
  // Create thumbnail source for company logo (base64 or server URL)
  const companyLogoThumbnail = useMemo(() => {
    // Company logo is stored as base64 data URL, so we can use it directly
    if (formData.companyLogo && formData.companyLogo.startsWith('data:')) {
      return formData.companyLogo;
    }
    // If we have a server URL, use getImageUrl
    if (formData.companyLogoUrl) {
      return getImageUrl(formData.companyLogoUrl);
    }
    return null;
  }, [formData.companyLogo, formData.companyLogoUrl]);
  
  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (bannerImageObjectUrlRef.current) {
        URL.revokeObjectURL(bannerImageObjectUrlRef.current);
      }
      if (companyLogoObjectUrlRef.current) {
        URL.revokeObjectURL(companyLogoObjectUrlRef.current);
      }
    };
  }, []);
  
  // Crop modal state for banners
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isReCropping, setIsReCropping] = useState(false); // Track if re-cropping existing image

  // Crop modal state for company logo (using react-image-crop)
  const [showCompanyLogoCropModal, setShowCompanyLogoCropModal] = useState(false);
  const [companyLogoCropImage, setCompanyLogoCropImage] = useState(null);
  const [companyLogoCropFileName, setCompanyLogoCropFileName] = useState('');
  const [isCompanyLogoReCropping, setIsCompanyLogoReCropping] = useState(false); // Track if re-cropping existing logo
  const [companyLogoCrop, setCompanyLogoCrop] = useState({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50
  });
  const companyLogoImgRef = useRef(null);

  // Load existing data when editing
  useEffect(() => {
    const loadEditData = async () => {
      if (isEditMode) {
        try {
          const pageToEdit = await getMarketingPage(editId);
          
          if (pageToEdit && pageToEdit.fullConfig) {
            // Load from fullConfig
            const { basic, banners: savedBanners, content } = pageToEdit.fullConfig;
            
            // Get company logo and convert to base64 for cropping support
            const companyLogoUrl = basic.companyLogoUrl ? getImageUrl(basic.companyLogoUrl) : null;
            const companyLogoBase64 = await urlToBase64(companyLogoUrl);
            
            setFormData({
              name: basic.name || '',
              description: basic.description || '',
              slogan: basic.slogan || '',
              companyLogo: companyLogoBase64, // Base64 for cropping
              companyLogoName: basic.companyLogoName || '',
              companyLogoUrl: basic.companyLogoUrl || null, // Store server path
              originalCompanyLogo: companyLogoBase64, // Base64 for re-cropping
              savedCompanyLogoCrop: basic.savedCompanyLogoCrop || null, // Restore saved crop settings
              isCompanyLogoModified: false // Track if logo was changed by user
            });
            
            // Load banner images and convert to base64 for cropping support
            const bannersWithImages = await Promise.all((savedBanners || []).map(async (banner) => {
              const imageUrl = banner.imageUrl ? getImageUrl(banner.imageUrl) : null;
              const imageBase64 = await urlToBase64(imageUrl);
              return {
                ...banner,
                imageUrl: banner.imageUrl, // Keep original server URL
                imageData: imageBase64, // Base64 for cropping
                originalImageUrl: imageBase64, // Base64 for re-cropping
                isImageModified: false // Track if image was changed by user
              };
            }));
            
            setBanners(bannersWithImages);
            
            setContentConfig({
              linkVoucherEnabled: content.linkVoucherEnabled || false,
              linkVoucherTitle: content.linkVoucherTitle || '',
              vouchers: content.vouchers || [],
              linkCampaignEnabled: content.linkCampaignEnabled || false,
              linkCampaignTitle: content.linkCampaignTitle || '',
              selectedCampaign: content.selectedCampaign || ''
            });
            
            // Load campaigns to restore campaign name if needed
            if (content.selectedCampaign) {
              loadCampaigns();
            }
          } else if (pageToEdit) {
            // Fallback for older records without fullConfig
            setFormData(prev => ({
              ...prev,
              name: pageToEdit.name || ''
            }));
          }
        } catch (error) {
          console.error('Error loading marketing page:', error);
          toast.error('Failed to load marketing page data');
        }
      }
    };
    
    loadEditData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editId]);

  const getVoucherTokenInfo = (item = {}) => {
    const token = item.token || item.claimCostToken || item.voucherSchema?.token || {};
    const tokenSymbol = token.symbol || token.tokenSymbol || item.tokenSymbol || '';
    const tokenName = token.name || token.tokenName || item.tokenName || tokenSymbol || 'Token';
    const tokenIcon =
      token.logo?.imageUrl ||
      token.icon ||
      token.image ||
      token.imageUrl ||
      token.logoUrl ||
      item.tokenIcon ||
      '';

    return { tokenName, tokenSymbol: tokenName || tokenSymbol || 'Token', tokenIcon };
  };

  const getVoucherTemplateId = (item = {}) => {
    const voucherSchema = item.voucherSchema || {};
    const nestedVoucherId =
      item.voucherId && typeof item.voucherId === 'object' && !Array.isArray(item.voucherId)
        ? item.voucherId._id
        : item.voucherId;

    return (
      voucherSchema.voucherSchemaId ||
      item.voucherSchemaId ||
      nestedVoucherId ||
      item.campaignVoucherSchemaId ||
      item._id ||
      item.id ||
      ''
    );
  };

  const serializeVoucherForPwaLink = (voucher = {}) => ({
    id: voucher.id || '',
    voucherSchemaId: voucher.voucherSchemaId || voucher.id || '',
    campaignId: voucher.campaignId || '',
    campaignVoucherId: voucher.campaignVoucherId || '',
    name: voucher.name || '',
    price: voucher.price || 0,
    imageUrl: voucher.imageUrl || '',
    tokenName: voucher.tokenName || voucher.tokenSymbol || 'Token',
    tokenSymbol: voucher.tokenName || voucher.tokenSymbol || 'Token',
    tokenIcon: voucher.tokenIcon || '',
    companyName: voucher.companyName || '',
    companyIcon: voucher.companyIcon || '',
    status: voucher.status || voucher._original?.voucherSchema?.status || voucher._original?.status || '',
  });

  // Helper function to transform API voucher data to our format
  const transformVoucherData = (apiData) => {
    if (!apiData?.message?.data) return [];
    const rows = apiData.message.data.filter(isVoucherListItemDisplayable);

    return rows.map(item => {
      const vs = item.voucherSchema || {};
      const startDate = vs.expiryStartDatetime ? new Date(vs.expiryStartDatetime).toLocaleDateString('en-GB') : '';
      const endDate = vs.expiryEndDatetime ? new Date(vs.expiryEndDatetime).toLocaleDateString('en-GB') : '';
      const validPeriod = startDate && endDate ? `${startDate} - ${endDate}` : '-';
      
      // Get brands (can have multiple)
      const brands = (item.brandList || []).map(b => b.brandName).join(', ') || '-';
      
      // Get categories (can have multiple)
      const categories = (item.categoryList || []).map(c => c.categoryName).join(', ') || '-';
      
      // Get image URL
      const imageUrl = vs.image?.imageUrl || '';
      
      // Get token info
      const tokenInfo = getVoucherTokenInfo(item);
      
      return {
        id: getVoucherTemplateId(item),
        voucherSchemaId: getVoucherTemplateId(item),
        campaignId: item.campaignId || '',
        campaignVoucherId: item.campaignVoucherId || '',
        name: vs.voucherSchemaName?.en || '-',
        type: vs.voucherType || '-',
        price: item.price || 0,
        brand: brands,
        quantity: item.quantity != null ? item.quantity : 'Infinite',
        validPeriod: validPeriod,
        expiryStartDatetime: vs.expiryStartDatetime || '',
        expiryEndDatetime: vs.expiryEndDatetime || '',
        category: categories,
        country: vs.country?.countryName || '-',
        imageUrl: imageUrl,
        tokenName: tokenInfo.tokenName,
        tokenSymbol: tokenInfo.tokenSymbol,
        tokenIcon: tokenInfo.tokenIcon,
        status: vs.status || item.status || '',
        // Keep original data for reference
        _original: item
      };
    });
  };

  // Fetch vouchers from API for voucher selection modals
  const loadVouchers = async (page = 1, forceRefresh = false, options = {}) => {
    if (apiVouchers.length > 0 && !forceRefresh && page === voucherPagination.page) return; // Already loaded

    const search = String(options.search ?? '').trim();
    setVouchersLoading(true);
    try {
      console.log('📋 Fetching vouchers from API, page:', page, 'search:', search);
      const response = await fetchPublishedVouchers(page, VOUCHER_MODAL_PAGE_SIZE, undefined, search);
      const transformed = transformVoucherData(response);
      const campaignLinkedVouchers = transformed.filter(v => v.campaignId);
      console.log('✅ Campaign vouchers loaded:', campaignLinkedVouchers.length);
      
      // Pagination from API; totalCount = campaign-linked, non-expired rows on this page.
      setVoucherPagination({
        page: response?.message?.page || page,
        size: response?.message?.size || VOUCHER_MODAL_PAGE_SIZE,
        totalCount: response?.message?.totalCount || campaignLinkedVouchers.length,
        lastPage: response?.message?.lastPage || 1
      });
      
      setApiVouchers(campaignLinkedVouchers);
    } catch (error) {
      console.error('❌ Failed to fetch vouchers:', error);
      toast.error('Failed to load vouchers');
    } finally {
      setVouchersLoading(false);
    }
  };

  useEffect(() => {
    if (!showVoucherModal) return undefined;

    const timeoutId = window.setTimeout(() => {
      loadVouchers(1, true, { search: contentVoucherSearch });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [showVoucherModal, contentVoucherSearch]);

  useEffect(() => {
    if (!showBannerVoucherModal) return undefined;

    const timeoutId = window.setTimeout(() => {
      loadVouchers(1, true, { search: bannerVoucherSearch });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [showBannerVoucherModal, bannerVoucherSearch]);

  // Fetch campaigns from API
  const loadCampaigns = async () => {
    if (apiCampaigns.length > 0) return; // Already loaded
    
    setCampaignsLoading(true);
    try {
      console.log('📋 Fetching campaigns from API...');
      const response = await fetchCampaigns(1, 100);
      
      if (response?.status === 'Success' && response.message?.data) {
        const campaigns = response.message.data.map(campaign => ({
          id: campaign.campaignId,
          name: campaign.name,
          publishedVoucherCount: campaign.publishedVoucherCount || 0,
          startDatetime: campaign.startDatetime,
          endDatetime: campaign.endDatetime
        }));
        console.log('✅ Campaigns loaded:', campaigns.length);
        setApiCampaigns(campaigns);
        
        // Fetch filtered voucher counts for all campaigns
        campaigns.forEach(campaign => {
          if (campaign.id) {
            loadCampaignVouchers(campaign.id, true);
          }
        });
      }
    } catch (error) {
      console.error('❌ Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Load campaign vouchers for modal
  const loadCampaignVouchers = async (campaignId, updateCountOnly = false) => {
    if (!campaignId) return;
    
    if (!updateCountOnly) {
      setCampaignVouchersLoading(true);
      setCampaignVouchers([]);
    }
    try {
      const response = await fetchCampaignVouchers(campaignId, 1, 100000);
      
      if (response?.status === 'Success' && response.message?.data) {
        const sourceRows = (response.message.data || []).filter(isVoucherListItemDisplayable);
        const vouchers = sourceRows.map(item => {
          const vs = item.voucherSchema || {};
          const startDate = vs.expiryStartDatetime ? new Date(vs.expiryStartDatetime).toLocaleDateString('en-GB') : '';
          const endDate = vs.expiryEndDatetime ? new Date(vs.expiryEndDatetime).toLocaleDateString('en-GB') : '';
          const validPeriod = startDate && endDate ? `${startDate} - ${endDate}` : '-';
          
          // Get brands (can have multiple) - check both item.brandList and vs.brands
          const brands = (item.brandList || []).map(b => b.brandName).join(', ') || 
                        (vs.brands || []).map(b => b.brandName).join(', ') || '-';
          
          // Get categories (can have multiple) - check both item.categoryList and vs.categories
          const categories = (item.categoryList || []).map(c => c.categoryName).join(', ') || 
                            (vs.categories || []).map(c => c.categoryName).join(', ') || '-';
          
          // Get image URL
          const imageUrl = vs.image?.imageUrl || '';
          
          // Get token info
          const tokenInfo = getVoucherTokenInfo(item);
          
          return {
            id: getVoucherTemplateId(item),
            voucherSchemaId: getVoucherTemplateId(item),
            campaignId: item.campaignId || selectedCampaignForVouchers?.id || campaignId || '',
            campaignVoucherId: item.campaignVoucherId || '',
            name: vs.voucherSchemaName?.en || '-',
            type: vs.voucherType || '-',
            price: item.price || 0,
            brand: brands,
            quantity: item.quantity != null ? item.quantity : 'Infinite',
            validPeriod: validPeriod,
            expiryStartDatetime: vs.expiryStartDatetime || '',
            expiryEndDatetime: vs.expiryEndDatetime || '',
            category: categories,
            country: vs.country?.countryName || '-',
            imageUrl: imageUrl,
            tokenName: tokenInfo.tokenName,
            tokenSymbol: tokenInfo.tokenSymbol,
            tokenIcon: tokenInfo.tokenIcon,
            status: vs.status || item.status || '',
          };
        });
        
        // Update filtered voucher count for this campaign
        setFilteredVoucherCount(prev => ({
          ...prev,
          [campaignId]: vouchers.length
        }));
        
        if (!updateCountOnly) {
          setCampaignVouchers(vouchers);
        }
      } else {
        // No vouchers found - set count to 0
        setFilteredVoucherCount(prev => ({
          ...prev,
          [campaignId]: 0
        }));
      }
    } catch (error) {
      console.error('❌ Failed to fetch campaign vouchers:', error);
      if (!updateCountOnly) {
        toast.error('Failed to load campaign vouchers');
      }
    } finally {
      if (!updateCountOnly) {
        setCampaignVouchersLoading(false);
      }
    }
  };

  // Open campaign vouchers modal
  const handleShowCampaignVouchers = (campaignId) => {
    const campaign = apiCampaigns.find(c => c.id === campaignId);
    setSelectedCampaignForVouchers(campaign);
    setShowCampaignVouchersModal(true);
    loadCampaignVouchers(campaignId);
  };

  // Get unique filter options from vouchers
  const getFilterOptions = (vouchers, field) => {
    const allValues = vouchers.flatMap(v => {
      const value = v[field];
      // Handle comma-separated values (for brand and category)
      if (typeof value === 'string' && value.includes(',')) {
        return value.split(',').map(s => s.trim());
      }
      return [value];
    });
    return [...new Set(allValues.filter(v => v && v !== '-'))];
  };

  // Filter vouchers based on search and filters
  const getFilteredVouchers = () => {
    return apiVouchers.filter(v => {
      if (!v.campaignId) return false;
      if (!isMarketingOpsVoucherRowDisplayable(v)) return false;
      const matchesSearch = v.name.toLowerCase().includes(bannerVoucherSearch.toLowerCase());
      const matchesBrand = !bannerVoucherFilters.brand || v.brand.includes(bannerVoucherFilters.brand);
      const matchesCategory = !bannerVoucherFilters.category || v.category.includes(bannerVoucherFilters.category);
      const matchesType = !bannerVoucherFilters.voucherType || v.type === bannerVoucherFilters.voucherType;
      const matchesCountry = !bannerVoucherFilters.country || v.country === bannerVoucherFilters.country;
      return matchesSearch && matchesBrand && matchesCategory && matchesType && matchesCountry;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate URL format
  const isValidUrl = (urlString) => {
    if (!urlString || urlString.trim() === '') return false;
    try {
      // Try to create a URL object - this validates the format
      const url = new URL(urlString);
      // Check if it's http or https
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (e) {
      return false;
    }
  };

  const handleBannerFormChange = (e) => {
    const { name, value } = e.target;
    
    // Validate URL if it's the link field and linkType is custom
    if (name === 'link' && bannerForm.linkType === 'custom') {
      if (value.trim() === '') {
        setBannerLinkError('');
      } else if (!isValidUrl(value)) {
        setBannerLinkError('Please enter a valid URL (e.g., https://example.com)');
      } else {
        setBannerLinkError('');
      }
    }
    
    setBannerForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, or WEBP)');
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      e.target.value = ''; // Reset input
      return;
    }

    // For GIF files, skip cropping to preserve animation
    if (file.type === 'image/gif') {
      // Store original image as data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerForm(prev => ({
          ...prev,
          image: file, // Store the file directly
          imageName: file.name,
          originalImageUrl: reader.result, // Store for display
          savedCrop: null,
          savedZoom: 1
        }));
        toast.info('GIF uploaded directly to preserve animation (no cropping)');
      };
      reader.readAsDataURL(file);
      return;
    }

    // For other formats, open crop modal with the image
    const imageUrl = URL.createObjectURL(file);
    // Store original image as data URL for re-cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setBannerForm(prev => ({
        ...prev,
        imageName: file.name,
        originalImageUrl: reader.result
      }));
    };
    reader.readAsDataURL(file);
    
    setCropImage(imageUrl);
    setShowCropModal(true);
    setIsReCropping(false); // This is a new upload
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { alpha: true });

    // Set canvas size to 800x800 (1:1 square ratio)
    canvas.width = 800;
    canvas.height = 800;

    // Clear canvas to transparent (important for PNG/WebP with transparency)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Determine output format based on original file type
    const fileName = bannerForm.imageName || '';
    const ext = fileName.toLowerCase().split('.').pop();
    let mimeType = 'image/png'; // Default to PNG to preserve transparency
    
    if (ext === 'jpg' || ext === 'jpeg') {
      mimeType = 'image/jpeg';
    } else if (ext === 'webp') {
      mimeType = 'image/webp';
    } else if (ext === 'gif') {
      // Note: GIF animation is lost when cropping via canvas
      // Keep as PNG to preserve any transparency
      mimeType = 'image/png';
    }
    // PNG is default for transparency support

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, mimeType, 0.95);
    });
  };

  const handleCropConfirm = async () => {
    if (!cropImage || !croppedAreaPixels) return;

    try {
      const croppedBlob = await getCroppedImg(cropImage, croppedAreaPixels);
      
      // Determine correct file extension based on blob type
      let fileName = bannerForm.imageName || 'cropped-image.jpg';
      const ext = fileName.toLowerCase().split('.').pop();
      let mimeType = croppedBlob.type || 'image/jpeg';
      
      // If extension doesn't match blob type, update filename
      if (mimeType === 'image/png' && ext !== 'png') {
        fileName = fileName.replace(/\.[^.]+$/, '.png');
      } else if (mimeType === 'image/webp' && ext !== 'webp') {
        fileName = fileName.replace(/\.[^.]+$/, '.webp');
      } else if (mimeType === 'image/gif' && ext !== 'gif') {
        fileName = fileName.replace(/\.[^.]+$/, '.gif');
      }
      
      const croppedFile = new File([croppedBlob], fileName, {
        type: mimeType,
      });

      // Save the current crop and zoom values for re-cropping
      setBannerForm(prev => ({
        ...prev,
        image: croppedFile,
        imageName: prev.imageName || 'cropped-image.jpg',
        savedCrop: { ...crop },
        savedZoom: zoom
      }));

      // Clean up
      URL.revokeObjectURL(cropImage);
      setShowCropModal(false);
      setCropImage(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } catch (error) {
      toast.error('Failed to crop image. Please try again.');
      console.error('Error cropping image:', error);
    }
  };

  const handleCropCancel = () => {
    if (cropImage) {
      URL.revokeObjectURL(cropImage);
    }
    setShowCropModal(false);
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    
    // Only reset banner form data if this was a new upload (not re-cropping)
    if (!isReCropping) {
      setBannerForm(prev => ({
        ...prev,
        image: null,
        imageName: '',
        originalImageUrl: null
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    setIsReCropping(false);
  };

  const getCompanyLogoCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Handle percentage vs pixel units
    let cropX, cropY, cropWidth, cropHeight;
    if (crop.unit === '%') {
      // Convert percentages to pixels based on displayed image size
      cropX = (crop.x / 100) * image.width;
      cropY = (crop.y / 100) * image.height;
      cropWidth = (crop.width / 100) * image.width;
      cropHeight = (crop.height / 100) * image.height;
    } else {
      // Already in pixels
      cropX = crop.x;
      cropY = crop.y;
      cropWidth = crop.width;
      cropHeight = crop.height;
    }
    
    // Scale to natural image dimensions
    canvas.width = cropWidth * scaleX;
    canvas.height = cropHeight * scaleY;
    
    const ctx = canvas.getContext('2d', { alpha: true });
    
    // Clear canvas to transparent (important for PNG/WebP with transparency)
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.drawImage(
      image,
      cropX * scaleX,
      cropY * scaleY,
      cropWidth * scaleX,
      cropHeight * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Determine output format based on original file type
    // Default to PNG to preserve transparency for logos
    const fileName = companyLogoCropFileName || '';
    const ext = fileName.toLowerCase().split('.').pop();
    let mimeType = 'image/png'; // Default to PNG to preserve transparency
    
    if (ext === 'jpg' || ext === 'jpeg') {
      mimeType = 'image/jpeg';
    } else if (ext === 'webp') {
      mimeType = 'image/webp';
    } else if (ext === 'gif') {
      // Note: GIF animation is lost when cropping via canvas
      // Use PNG to preserve any transparency
      mimeType = 'image/png';
    }
    // PNG is default for transparency support
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, mimeType, 0.95);
    });
  };

  const handleCompanyLogoCropConfirm = async () => {
    if (!companyLogoImgRef.current || !companyLogoCrop.width || !companyLogoCrop.height) {
      toast.error('Please select a crop area');
      return;
    }

    try {
      const croppedBlob = await getCompanyLogoCroppedImg(companyLogoImgRef.current, companyLogoCrop);
      
      // Save the current crop settings for re-cropping
      const savedCrop = { ...companyLogoCrop };
      
      // Determine correct filename with extension based on blob type
      let fileName = companyLogoCropFileName || 'cropped-logo.jpg';
      const ext = fileName.toLowerCase().split('.').pop();
      const mimeType = croppedBlob.type || 'image/jpeg';
      
      // If extension doesn't match blob type, update filename
      if (mimeType === 'image/png' && ext !== 'png') {
        fileName = fileName.replace(/\.[^.]+$/, '.png');
      } else if (mimeType === 'image/webp' && ext !== 'webp') {
        fileName = fileName.replace(/\.[^.]+$/, '.webp');
      } else if (mimeType === 'image/gif' && ext !== 'gif') {
        fileName = fileName.replace(/\.[^.]+$/, '.gif');
      }
      
      // Convert blob to data URL for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          companyLogo: reader.result,
          companyLogoName: fileName,
          savedCompanyLogoCrop: savedCrop,
          isCompanyLogoModified: true, // Mark as modified when cropped/uploaded
          companyLogoUrl: isCompanyLogoReCropping ? prev.companyLogoUrl : undefined // Clear URL if new upload
        }));
      };
      reader.readAsDataURL(croppedBlob);

      // Clean up
      URL.revokeObjectURL(companyLogoCropImage);
      setShowCompanyLogoCropModal(false);
      setCompanyLogoCropImage(null);
      setCompanyLogoCropFileName('');
      setCompanyLogoCrop({ unit: '%', x: 25, y: 25, width: 50, height: 50 });
    } catch (error) {
      toast.error('Failed to crop image. Please try again.');
      console.error('Error cropping company logo:', error);
    }
  };

  const handleCompanyLogoCropCancel = () => {
    if (companyLogoCropImage) {
      URL.revokeObjectURL(companyLogoCropImage);
    }
    setShowCompanyLogoCropModal(false);
    setCompanyLogoCropImage(null);
    setCompanyLogoCropFileName('');
    setCompanyLogoCrop({ unit: '%', x: 25, y: 25, width: 50, height: 50 });
    
    // Only reset form data if this was a new upload (not re-cropping)
    if (!isCompanyLogoReCropping) {
      if (companyLogoInputRef.current) {
        companyLogoInputRef.current.value = '';
      }
      setFormData(prev => ({
        ...prev,
        companyLogo: null,
        companyLogoName: '',
        originalCompanyLogo: null,
        savedCompanyLogoCrop: null
      }));
    }
    setIsCompanyLogoReCropping(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCompanyLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - JPG and PNG only for company logo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG or PNG)');
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      e.target.value = ''; // Reset input
      return;
    }

    // GIF handling removed - company logo only accepts JPG/PNG
    // For GIF files, skip cropping to preserve animation
    if (file.type === 'image/gif') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          companyLogo: reader.result, // Store as data URL
          companyLogoName: file.name,
          originalCompanyLogo: reader.result,
          savedCompanyLogoCrop: null,
          isCompanyLogoModified: true
        }));
        toast.info('GIF uploaded directly to preserve animation (no cropping)');
      };
      reader.readAsDataURL(file);
      return;
    }

    // For other formats, open crop modal with the image
    const imageUrl = URL.createObjectURL(file);
    setCompanyLogoCropFileName(file.name);
    setCompanyLogoCropImage(imageUrl);
    setShowCompanyLogoCropModal(true);
    setIsCompanyLogoReCropping(false); // This is a new upload
    setCompanyLogoCrop({ unit: '%', x: 25, y: 25, width: 50, height: 50 });
    
    // Store original image as data URL for re-cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        originalCompanyLogo: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleCompanyLogoUploadClick = () => {
    companyLogoInputRef.current?.click();
  };

  const openBannerModal = () => {
    // Check if maximum banners limit reached
    if (banners.length >= 5) {
      toast.error('Maximum 5 banners allowed');
      return;
    }
    setEditingBannerId(null);
    setBannerForm({ title: '', image: null, imageName: '', originalImageUrl: null, savedCrop: null, savedZoom: 1, linkType: 'voucher', selectedVoucher: '', selectedVoucherData: null, link: '' });
    setBannerLinkError('');
    setShowBannerModal(true);
  };

  const openEditBannerModal = (banner) => {
    setEditingBannerId(banner.id);
    const linkType = banner.linkType || 'custom';
    const link = banner.link || '';
    
    // Validate URL if editing with custom link
    if (linkType === 'custom' && link) {
      if (!isValidUrl(link)) {
        setBannerLinkError('Please enter a valid URL (e.g., https://example.com)');
      } else {
        setBannerLinkError('');
      }
    } else {
      setBannerLinkError('');
    }
    
    setBannerForm({
      title: banner.title,
      image: null,
      imageName: banner.image,
      originalImageUrl: banner.originalImageData || banner.imageData || null, // Use original uncropped image for re-cropping
      savedCrop: banner.savedCrop || null,
      savedZoom: banner.savedZoom || 1,
      linkType: linkType,
      selectedVoucher: banner.selectedVoucher || '',
      selectedVoucherData: banner.selectedVoucherData || null,
      link: link
    });
    setShowBannerModal(true);
  };

  const closeBannerModal = () => {
    setShowBannerModal(false);
    setEditingBannerId(null);
    setBannerForm({ title: '', image: null, imageName: '', originalImageUrl: null, savedCrop: null, savedZoom: 1, linkType: 'voucher', selectedVoucher: '', selectedVoucherData: null, link: '' });
    setBannerLinkError('');
  };

  const handleBannerSubmit = () => {
    // Validate custom link URL if applicable
    if (bannerForm.linkType === 'custom' && bannerForm.link) {
      if (!isValidUrl(bannerForm.link)) {
        setBannerLinkError('Please enter a valid URL (e.g., https://example.com)');
        toast.error('Please enter a valid URL for the banner link');
        return;
      }
    }
    
    const isValidLink = bannerForm.linkType === 'voucher' 
      ? bannerForm.selectedVoucher 
      : (bannerForm.link && !bannerLinkError);
    
    if (bannerForm.title && bannerForm.imageName && isValidLink) {
      const processImage = (imageFile, imageName) => {
        if (imageFile) {
          // If there's a new file, convert it to data URL
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                dataUrl: reader.result,
                name: imageName
              });
            };
            reader.readAsDataURL(imageFile);
          });
        } else {
          // If no new file, check if we have existing image data
          const existingBanner = banners.find(b => b.id === editingBannerId);
          if (existingBanner && existingBanner.imageData) {
            return Promise.resolve({
              dataUrl: existingBanner.imageData,
              name: imageName
            });
          }
          // Fallback to just name if no image data
          return Promise.resolve({
            dataUrl: null,
            name: imageName
          });
        }
      };

      processImage(bannerForm.image, bannerForm.imageName).then((imageData) => {
        // Get original image URL (for re-cropping later)
        let originalImage = bannerForm.originalImageUrl;
        if (editingBannerId) {
          // If editing, preserve the existing original
          const existingBanner = banners.find(b => b.id === editingBannerId);
          if (existingBanner && existingBanner.originalImageData) {
            originalImage = existingBanner.originalImageData;
          }
        }
        
        // Check if image was actually changed (new upload or re-crop)
        const existingBanner = editingBannerId ? banners.find(b => b.id === editingBannerId) : null;
        const isImageChanged = bannerForm.image || // New file uploaded
                              (existingBanner && imageData.dataUrl !== existingBanner.imageData); // Re-cropped
        
        const selectedVoucher = bannerForm.linkType === 'voucher' && bannerForm.selectedVoucher
          ? apiVouchers.find(v => v.id === bannerForm.selectedVoucher)
          : null;
        const selectedVoucherData = selectedVoucher
          ? serializeVoucherForPwaLink(selectedVoucher)
          : bannerForm.selectedVoucherData || null;
        // Get voucher name for display purposes
        const selectedVoucherName = bannerForm.linkType === 'voucher' && bannerForm.selectedVoucher
          ? selectedVoucherData?.name || ''
          : '';
        
        const bannerData = {
          title: bannerForm.title,
          image: imageData.name,
          imageData: imageData.dataUrl, // Cropped image
          originalImageData: originalImage, // Original uncropped image for re-cropping
          savedCrop: bannerForm.savedCrop,
          savedZoom: bannerForm.savedZoom,
          linkType: bannerForm.linkType,
          selectedVoucher: bannerForm.selectedVoucher || '',
          selectedVoucherData: bannerForm.linkType === 'voucher' ? selectedVoucherData : null,
          selectedVoucherName: selectedVoucherName, // Store voucher name for display
          link: bannerForm.link || '',
          isImageModified: isImageChanged // Mark as modified if image changed
        };

        if (editingBannerId) {
          // Edit existing banner - preserve imageUrl if image wasn't changed
          setBanners(banners.map(b => 
            b.id === editingBannerId 
              ? { 
                  ...b, 
                  ...bannerData,
                  imageUrl: isImageChanged ? undefined : b.imageUrl // Clear imageUrl if changed, keep if not
                }
              : b
          ));
        } else {
          // Add new banner - check limit
          if (banners.length >= 5) {
            toast.error('Maximum 5 banners allowed');
            return;
          }
          const newBanner = {
            id: Date.now(),
            ...bannerData,
            isImageModified: true // New images are always modified
          };
          setBanners([...banners, newBanner]);
        }
        closeBannerModal();
      });
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleCreate = async () => {
    try {
      // Upload banner images and prepare banner data
      const bannersForStorage = await Promise.all(banners.map(async (banner) => {
        let imageUrl = banner.imageUrl || null; // Keep existing URL if editing
        
        // Only upload if:
        // 1. imageData exists (base64) AND
        // 2. No existing imageUrl (new image) OR image was modified (re-cropped or replaced)
        const isNewImage = !banner.imageUrl;
        const isModified = banner.isImageModified === true;
        
        if (banner.imageData && banner.imageData.startsWith('data:') && (isNewImage || isModified)) {
          const uploadResult = await uploadBase64Image(banner.imageData, banner.image || 'banner.jpg', 'banner');
          imageUrl = uploadResult.imageUrl;
        }
        
        return {
          id: banner.id,
          title: banner.title,
          image: banner.image, // Filename for display
          imageUrl: imageUrl, // Server path
          linkType: banner.linkType,
          selectedVoucher: banner.selectedVoucher || '',
          selectedVoucherData: banner.selectedVoucherData || null,
          selectedVoucherName: banner.selectedVoucherName || '', // Store voucher name for display
          link: banner.link || ''
        };
      }));
      
      // Upload company logo if it's a new base64 image
      // Only upload if logo was actually changed (not just loaded from server)
      let companyLogoUrl = formData.companyLogoUrl || null;
      const isLogoModified = formData.isCompanyLogoModified === true;
      const isNewLogo = !formData.companyLogoUrl;
      
      if (formData.companyLogo && formData.companyLogo.startsWith('data:') && (isNewLogo || isLogoModified)) {
        const uploadResult = await uploadBase64Image(formData.companyLogo, formData.companyLogoName || 'logo.jpg', 'logo');
        companyLogoUrl = uploadResult.imageUrl;
      }
      
      const formDataForStorage = {
        name: formData.name,
        description: formData.description,
        slogan: formData.slogan,
        companyLogoName: formData.companyLogoName || '',
        companyLogoUrl: companyLogoUrl,
        savedCompanyLogoCrop: formData.savedCompanyLogoCrop || null // Save crop settings
      };
      
      const contentForStorage = {
        linkVoucherEnabled: contentConfig.linkVoucherEnabled,
        linkVoucherTitle: contentConfig.linkVoucherTitle,
        vouchers: contentConfig.vouchers.map(v => ({
          id: v.id,
          voucherSchemaId: v.voucherSchemaId || v.id,
          campaignId: v.campaignId || '',
          campaignVoucherId: v.campaignVoucherId || '',
          name: v.name,
          type: v.type || '-',
          price: v.price || 0,
          brand: v.brand || '-',
          quantity: v.quantity === 'Infinite' ? 'Infinite' : (v.quantity || 0),
          validPeriod: v.validPeriod || '-',
          expiryStartDatetime: v.expiryStartDatetime || '',
          expiryEndDatetime: v.expiryEndDatetime || '',
          category: v.category || '-',
          country: v.country || '-',
          imageUrl: v.imageUrl || '',
          tokenName: v.tokenName || v.tokenSymbol || 'Token',
          tokenSymbol: v.tokenName || v.tokenSymbol || 'Token',
          tokenIcon: v.tokenIcon || '',
          status: v.status || ''
        })),
        linkCampaignEnabled: contentConfig.linkCampaignEnabled,
        linkCampaignTitle: contentConfig.linkCampaignTitle,
        selectedCampaign: contentConfig.selectedCampaign,
        selectedCampaignName: apiCampaigns.find(c => c.id === contentConfig.selectedCampaign)?.name || ''
      };
      
      const pageData = {
        name: formData.name,
        bannerCount: banners.length,
        voucherCount: contentConfig.vouchers.length,
        campaignLink: contentConfig.selectedCampaign || '-',
        fullConfig: {
          basic: formDataForStorage,
          banners: bannersForStorage,
          content: contentForStorage
        }
      };
      
      if (isEditMode) {
        // Update existing marketing page
        await updateMarketingPage(editId, pageData);
        toast.success('Marketing page updated successfully!');
      } else {
        // Create new marketing page
        await createMarketingPage(pageData);
        toast.success('Marketing page created successfully!');
      }

      // Navigate to landing page
      navigate('/');
    } catch (error) {
      console.error('Error saving marketing page:', error);
      toast.error('Failed to save marketing page. Please try again.');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = () => {
    setBanners(banners.filter(banner => banner.id !== deleteConfirmId));
    setDeleteConfirmId(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  // Voucher delete handlers
  const handleDeleteVoucherClick = (voucherId) => {
    setDeleteVoucherConfirmId(voucherId);
  };

  const handleConfirmVoucherDelete = () => {
    setContentConfig(prev => ({
      ...prev,
      vouchers: prev.vouchers.filter(v => v.id !== deleteVoucherConfirmId)
    }));
    setDeleteVoucherConfirmId(null);
  };

  const handleCancelVoucherDelete = () => {
    setDeleteVoucherConfirmId(null);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newBanners = [...banners];
    const draggedBanner = newBanners[draggedIndex];
    newBanners.splice(draggedIndex, 1);
    newBanners.splice(index, 0, draggedBanner);
    setBanners(newBanners);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Voucher modal handlers
  const openVoucherModal = () => {
    setSelectedVouchers([]);
    setVoucherSearch('');
    setContentVoucherSearch('');
    setContentVoucherFilters({ brand: '', category: '', voucherType: '', country: '' });
    setShowVoucherModal(true);
  };

  const closeVoucherModal = () => {
    setShowVoucherModal(false);
    setSelectedVouchers([]);
    setVoucherSearch('');
    setContentVoucherSearch('');
    setContentVoucherFilters({ brand: '', category: '', voucherType: '', country: '' });
  };

  const toggleVoucherSelection = (voucher) => {
    setSelectedVouchers(prev => {
      const exists = prev.find(v => v.id === voucher.id);
      if (exists) {
        return prev.filter(v => v.id !== voucher.id);
      } else {
        return [...prev, voucher];
      }
    });
  };

  const handleAddSelectedVouchers = () => {
    // Filter out vouchers that are already added
    const existingIds = contentConfig.vouchers.map(v => v.id);
    const newVouchers = selectedVouchers.filter(v => !existingIds.includes(v.id));
    
    setContentConfig(prev => ({
      ...prev,
      vouchers: [...prev.vouchers, ...newVouchers]
    }));
    closeVoucherModal();
  };

  // Filter vouchers for content configuration modal
  const getContentFilteredVouchers = () => {
    return apiVouchers.filter(v => {
      if (!isMarketingOpsVoucherRowDisplayable(v)) return false;
      const matchesSearch = v.name.toLowerCase().includes(contentVoucherSearch.toLowerCase());
      const matchesBrand = !contentVoucherFilters.brand || v.brand.includes(contentVoucherFilters.brand);
      const matchesCategory = !contentVoucherFilters.category || v.category.includes(contentVoucherFilters.category);
      const matchesType = !contentVoucherFilters.voucherType || v.type === contentVoucherFilters.voucherType;
      const matchesCountry = !contentVoucherFilters.country || v.country === contentVoucherFilters.country;
      return matchesSearch && matchesBrand && matchesCategory && matchesType && matchesCountry;
    });
  };

  // Filtered vouchers for search (currently unused but kept for future use)
  // const filteredVouchers = apiVouchers.filter(v =>
  //   v.name.toLowerCase().includes(voucherSearch.toLowerCase())
  // );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="form-section">
              <h2 className="section-title">Basic Setting</h2>
              
              <div className="form-group">
                <label className="form-label">Marketing Page Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter marketing page name"
                  maxLength={30}
                />
                <span className="char-count">{formData.name.length}/30</span>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  rows={6}
                  maxLength={200}
                />
                <span className="char-count">{formData.description.length}/200</span>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            <div className="form-section">
              <h2 className="section-title">Banner Details</h2>
              
              <div className="banner-header">
                <button 
                  className="btn-add-banner" 
                  onClick={openBannerModal}
                  disabled={banners.length >= 5}
                >
                  <IconPlus size={16} stroke={2} />
                  Add Banner
                </button>
              </div>

              <div className="banner-table">
                <div className="banner-table-header">
                  <span className="col-drag"></span>
                  <span className="col-title">Banner Title</span>
                  <span className="col-image">Banner Image</span>
                  <span className="col-link">Banner Link</span>
                  <span className="col-actions"></span>
                </div>
                {banners.map((banner, index) => {
                  const getLinkDisplay = () => {
                    if (banner.linkType === 'voucher' && banner.selectedVoucher) {
                      // Try to get from API vouchers first, then stored name, then show ID
                      const voucher = apiVouchers.find(v => v.id === banner.selectedVoucher);
                      return voucher?.name || banner.selectedVoucherName || `Voucher: ${banner.selectedVoucher}`;
                    }
                    return banner.link || '-';
                  };
                  return (
                    <div
                      key={banner.id}
                      className={`banner-row ${draggedIndex === index ? 'dragging' : ''}`}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <span className="col-drag">
                        <IconGripVertical size={18} stroke={1.5} className="drag-handle" />
                      </span>
                      <span className="col-title">{banner.title || '-'}</span>
                      <span className="col-image">
                        {(() => {
                          // Get image source: server URL or base64
                          let imageSrc = null;
                          if (banner.imageUrl) {
                            imageSrc = getImageUrl(banner.imageUrl);
                          } else if (banner.imageData && banner.imageData.startsWith('data:')) {
                            imageSrc = banner.imageData;
                          }
                          
                          return imageSrc ? (
                            <div className="banner-table-thumbnail-wrapper">
                              <img 
                                src={imageSrc} 
                                alt={banner.image || 'Banner'} 
                                className="banner-table-thumbnail"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'inline';
                                  }
                                }}
                              />
                              <span className="banner-table-filename">{banner.image || '-'}</span>
                            </div>
                          ) : (
                            <span>{banner.image || '-'}</span>
                          );
                        })()}
                      </span>
                      <span className="col-link" title={getLinkDisplay()}>{getLinkDisplay()}</span>
                      <span className="col-actions">
                        <button 
                          className="btn-icon-action" 
                          onClick={() => openEditBannerModal(banner)}
                          title="Edit"
                      >
                        <IconEdit size={16} stroke={1.5} />
                      </button>
                      <button 
                        className="btn-icon-action" 
                        onClick={() => handleDeleteClick(banner.id)}
                        title="Delete"
                      >
                        <IconTrash size={16} stroke={1.5} />
                      </button>
                      </span>
                    </div>
                  );
                })}
                {banners.length === 0 && (
                  <div className="banner-row empty">
                    <span className="empty-message">No banners added yet</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Slogan</label>
                <input
                  type="text"
                  name="slogan"
                  className="form-input"
                  value={formData.slogan}
                  onChange={handleInputChange}
                  placeholder="Enter slogan"
                  maxLength={30}
                />
                <span className="char-count">{formData.slogan.length}/30</span>
              </div>

              <div className="form-group">
                <label className="form-label">Company Logo</label>
                <div className="upload-area-wrapper">
                  <input
                    type="file"
                    ref={companyLogoInputRef}
                    onChange={handleCompanyLogoUpload}
                    accept="image/jpeg,image/jpg,image/png"
                    style={{ display: 'none' }}
                  />
                  {formData.companyLogoName ? (
                    <div 
                      className="upload-area banner-image-preview"
                      onClick={() => {
                        // Use original uncropped image for re-cropping
                        if (formData.originalCompanyLogo) {
                          setCompanyLogoCropImage(formData.originalCompanyLogo);
                          setCompanyLogoCropFileName(formData.companyLogoName || 'logo.jpg'); // Set filename for format detection
                          setShowCompanyLogoCropModal(true);
                          setIsCompanyLogoReCropping(true); // This is re-cropping existing image
                          // Restore saved crop settings if available
                          setCompanyLogoCrop(formData.savedCompanyLogoCrop || { unit: '%', x: 25, y: 25, width: 50, height: 50 });
                        }
                      }}
                    >
                      <div className="banner-preview-content">
                        {companyLogoThumbnail ? (
                          <img 
                            src={companyLogoThumbnail} 
                            alt={formData.companyLogoName} 
                            className="company-logo-thumbnail"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <span className="upload-filename">{formData.companyLogoName}</span>
                      </div>
                      <button
                        type="button"
                        className="upload-remove-text-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({
                            ...prev,
                            companyLogo: null,
                            companyLogoName: '',
                            originalCompanyLogo: null,
                            savedCompanyLogoCrop: null
                          }));
                          if (companyLogoInputRef.current) {
                            companyLogoInputRef.current.value = '';
                          }
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area" onClick={handleCompanyLogoUploadClick}>
                      <IconUpload size={24} stroke={1.5} />
                      <span>Click to upload image</span>
                      <span className="upload-criteria">JPG or PNG (Max 5MB)</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            <div className="form-section">
              <h2 className="section-title">Content Configuration</h2>
              
              {/* Link Voucher Section */}
              <div className="config-section">
                <div className="config-header">
                  <span className="config-label">Link Voucher</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={contentConfig.linkVoucherEnabled}
                      onChange={(e) => setContentConfig(prev => ({
                        ...prev,
                        linkVoucherEnabled: e.target.checked
                      }))}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {contentConfig.linkVoucherEnabled && (
                  <div className="config-content">
                    <div className="form-group">
                      <label className="form-label">Voucher section title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={contentConfig.linkVoucherTitle}
                        onChange={(e) => setContentConfig(prev => ({
                          ...prev,
                          linkVoucherTitle: e.target.value
                        }))}
                        placeholder="Enter title"
                        maxLength={30}
                      />
                      <span className="char-count">{contentConfig.linkVoucherTitle.length}/30</span>
                    </div>

                    <div className="banner-header">
                      <button className="btn-add-banner" onClick={openVoucherModal}>
                        <IconPlus size={16} stroke={2} />
                        Add Voucher
                      </button>
                    </div>

                    <div className="voucher-list-table">
                      <div className="voucher-list-header">
                        <span>Voucher</span>
                        <span>Voucher Type</span>
                        <span>Price</span>
                        <span>Brand</span>
                        <span>Available Quantity</span>
                        <span>Validity Period</span>
                        <span>Category</span>
                        <span>Country</span>
                        <span className="col-actions"></span>
                      </div>
                      {contentConfig.vouchers.length === 0 ? (
                        <div className="voucher-list-row empty">
                          <span className="empty-message">No vouchers added yet</span>
                        </div>
                      ) : (
                        contentConfig.vouchers.map((voucher, index) => (
                          <div key={index} className="voucher-list-row">
                            <span>{voucher.name}</span>
                            <span>{voucher.type || '-'}</span>
                            <span>{voucher.price || '-'}</span>
                            <span>{voucher.brand || '-'}</span>
                            <span>{voucher.quantity}</span>
                            <span>{voucher.validPeriod || '-'}</span>
                            <span>{voucher.category || '-'}</span>
                            <span>{voucher.country || '-'}</span>
                            <span className="col-actions">
                              <button 
                                className="btn-icon-action" 
                                title="Delete"
                                onClick={() => handleDeleteVoucherClick(voucher.id)}
                              >
                                <IconTrash size={16} stroke={1.5} />
                              </button>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Link Campaign Section */}
              <div className="config-section">
                <div className="config-header">
                  <span className="config-label">Link Campaign</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={contentConfig.linkCampaignEnabled}
                      onChange={(e) => {
                        const isEnabled = e.target.checked;
                        setContentConfig(prev => ({
                          ...prev,
                          linkCampaignEnabled: isEnabled
                        }));
                        // Load campaigns when toggle is enabled
                        if (isEnabled) {
                          loadCampaigns();
                        }
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {contentConfig.linkCampaignEnabled && (
                  <div className="config-content">
                    <div className="form-group">
                      <label className="form-label">Campaign section title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={contentConfig.linkCampaignTitle}
                        onChange={(e) => setContentConfig(prev => ({
                          ...prev,
                          linkCampaignTitle: e.target.value
                        }))}
                        placeholder="Enter title"
                        maxLength={30}
                      />
                      <span className="char-count">{contentConfig.linkCampaignTitle.length}/30</span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Campaign</label>
                      <div className="campaign-select-wrapper">
                        <select
                          className="form-select campaign-select"
                          value={contentConfig.selectedCampaign}
                          onChange={(e) => {
                            const campaignId = e.target.value;
                            setContentConfig(prev => ({
                              ...prev,
                              selectedCampaign: campaignId
                            }));
                            // Fetch filtered voucher count for the selected campaign
                            if (campaignId) {
                              loadCampaignVouchers(campaignId, true);
                            }
                          }}
                          onFocus={loadCampaigns}
                        >
                          <option value="">{campaignsLoading ? 'Loading campaigns...' : 'Select Campaign'}</option>
                          {apiCampaigns.map(campaign => {
                            // Use filtered count if available, otherwise fall back to publishedVoucherCount
                            const voucherCount = filteredVoucherCount[campaign.id] ?? campaign.publishedVoucherCount ?? 0;
                            const hasVouchers = voucherCount > 0;
                            return (
                              <option 
                                key={campaign.id} 
                                value={campaign.id}
                                disabled={!hasVouchers}
                                data-name={campaign.name}
                              >
                                {campaign.name} ({voucherCount} {voucherCount === 1 ? 'voucher' : 'vouchers'})
                              </option>
                            );
                          })}
                        </select>
                        {contentConfig.selectedCampaign && (
                          <span className="campaign-select-display">
                            {apiCampaigns.find(c => c.id === contentConfig.selectedCampaign)?.name || ''}
                          </span>
                        )}
                      </div>
                      {contentConfig.selectedCampaign && (
                        <span 
                          className="field-footnote clickable-voucher-count"
                          onClick={() => handleShowCampaignVouchers(contentConfig.selectedCampaign)}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {filteredVoucherCount[contentConfig.selectedCampaign] ?? apiCampaigns.find(c => c.id === contentConfig.selectedCampaign)?.publishedVoucherCount ?? 0} vouchers included
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="step-content">
            <div className="form-section">
              <div className="review-header">
                <h2 className="section-title">{isEditMode ? 'Confirmation of changes' : 'Confirmation of creation'}</h2>
                <p className="review-subtitle">Please review the details before {isEditMode ? 'saving' : 'creating'} the marketing page.</p>
              </div>
              
              {/* Basic Setting Review */}
              <div className="review-section">
                <h3 className="review-section-title">Basic Setting</h3>
                <div className="review-grid">
                  <div className="review-item">
                    <span className="review-label">Marketing page name</span>
                    <span className="review-value">{formData.name}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Description</span>
                    <span className="review-value">{formData.description}</span>
                  </div>
                </div>
              </div>

              {/* Banner Details Review */}
              <div className="review-section">
                <h3 className="review-section-title">Banner Details</h3>
                {banners.length > 0 && (
                  <div className="review-table review-banner-table">
                    <div className="review-table-header review-banner-header">
                      <span>Order</span>
                      <span>Banner title</span>
                      <span>Banner image</span>
                      <span>Banner link</span>
                    </div>
                    {banners.map((banner, index) => {
                      const getLinkDisplay = () => {
                        if (banner.linkType === 'voucher' && banner.selectedVoucher) {
                          // Try to get from API vouchers first, then stored name, then show ID
                          const voucher = apiVouchers.find(v => v.id === banner.selectedVoucher);
                          return voucher?.name || banner.selectedVoucherName || `Voucher: ${banner.selectedVoucher}`;
                        }
                        return banner.link || '-';
                      };
                      // Get image source for thumbnail
                      const getBannerImageSrc = () => {
                        if (banner.imageUrl) {
                          return getImageUrl(banner.imageUrl);
                        } else if (banner.imageData && banner.imageData.startsWith('data:')) {
                          return banner.imageData;
                        }
                        return null;
                      };
                      
                      const bannerImageSrc = getBannerImageSrc();
                      
                      return (
                        <div key={index} className="review-table-row review-banner-row">
                          <span>{index + 1}</span>
                          <span>{banner.title}</span>
                          <span>
                            {bannerImageSrc ? (
                              <div className="banner-table-thumbnail-wrapper">
                                <img 
                                  src={bannerImageSrc} 
                                  alt={banner.image || 'Banner'} 
                                  className="banner-table-thumbnail"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                      e.target.nextSibling.style.display = 'block';
                                    }
                                  }}
                                />
                                <span className="banner-table-filename">{banner.image || '-'}</span>
                              </div>
                            ) : (
                              <span>{banner.image || '-'}</span>
                            )}
                          </span>
                          <span>{getLinkDisplay()}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="review-grid" style={{ marginTop: '1rem' }}>
                  <div className="review-item">
                    <span className="review-label">Slogan</span>
                    <span className="review-value">{formData.slogan}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Company Logo</span>
                    <div className="review-value company-logo-display">
                      {companyLogoThumbnail ? (
                        <>
                          <img 
                            src={companyLogoThumbnail} 
                            alt="Company Logo" 
                            className="company-logo-thumbnail"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'inline';
                              }
                            }}
                          />
                          <span className="company-logo-name">{formData.companyLogoName || '-'}</span>
                        </>
                      ) : (
                        <span>{formData.companyLogoName || '-'}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Configuration Review */}
              <div className="review-section">
                <h3 className="review-section-title">Content Configuration</h3>
                
                {/* Link Voucher */}
                <div className="review-subsection">
                  <h4 className="review-subsection-title">Link Voucher</h4>
                  {contentConfig.linkVoucherEnabled ? (
                    <>
                      <div className="review-grid">
                        <div className="review-item">
                          <span className="review-label">Voucher section title</span>
                          <span className="review-value">{contentConfig.linkVoucherTitle}</span>
                        </div>
                        <div className="review-item">
                          <span className="review-label">Number of vouchers</span>
                          <span className="review-value">{contentConfig.vouchers.length}</span>
                        </div>
                      </div>
                      {contentConfig.vouchers.length > 0 && (
                        <div className="review-table review-table-wide">
                          <div className="review-table-header review-voucher-header">
                            <span>Voucher</span>
                            <span>Voucher Type</span>
                            <span>Price</span>
                            <span>Brand</span>
                            <span>Available Quantity</span>
                            <span>Validity Period</span>
                            <span>Category</span>
                            <span>Country</span>
                          </div>
                          {contentConfig.vouchers.map((voucher, index) => (
                            <div key={index} className="review-table-row review-voucher-row">
                              <span>{voucher.name || '-'}</span>
                              <span>{voucher.type || '-'}</span>
                              <span>{voucher.price || '-'}</span>
                              <span>{voucher.brand || '-'}</span>
                              <span>{voucher.quantity}</span>
                              <span>{voucher.validPeriod || '-'}</span>
                              <span>{voucher.category || '-'}</span>
                              <span>{voucher.country || '-'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="review-value">-</span>
                  )}
                </div>

                {/* Link Campaign */}
                <div className="review-subsection">
                  <h4 className="review-subsection-title">Link Campaign</h4>
                  {contentConfig.linkCampaignEnabled ? (
                    <div className="review-grid">
                      <div className="review-item">
                        <span className="review-label">Campaign section title</span>
                        <span className="review-value">{contentConfig.linkCampaignTitle}</span>
                      </div>
                      <div className="review-item">
                        <span className="review-label">Selected campaign</span>
                        <div className="review-value">
                          <div>
                            {apiCampaigns.find(c => c.id === contentConfig.selectedCampaign)?.name || contentConfig.selectedCampaign}
                          </div>
                          {(() => {
                            const campaign = apiCampaigns.find(c => c.id === contentConfig.selectedCampaign);
                            const voucherCount = filteredVoucherCount[contentConfig.selectedCampaign] ?? campaign?.publishedVoucherCount ?? 0;
                            return (
                              <div 
                                style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.25rem', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => handleShowCampaignVouchers(contentConfig.selectedCampaign)}
                              >
                                {voucherCount} {voucherCount === 1 ? 'voucher' : 'vouchers'}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="review-value">-</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const contentModalVoucherCount = showVoucherModal ? voucherPagination.totalCount : 0;
  const bannerModalVoucherCount = showBannerVoucherModal ? voucherPagination.totalCount : 0;

  return (
    <div className="create-marketing-page">
      {/* Header */}
      <div className="create-header">
        <span className="breadcrumb-link" onClick={handleCancel}>Marketing page</span>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">{isEditMode ? 'Edit Marketing Page' : 'Create New Marketing Page'}</span>
      </div>

      {/* Content */}
      <div className="create-content">
        <div className="form-card">
          {/* Stepper */}
          <div className="stepper">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className={`step ${currentStep === step.id ? 'current' : ''} ${currentStep > step.id ? 'completed' : ''}`}>
                  <div className="step-circle">
                    {currentStep > step.id ? <IconCheck size={14} stroke={3} /> : step.id}
                  </div>
                  <span className="step-label">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`step-line ${currentStep > step.id ? 'completed' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Form Content */}
          {renderStepContent()}

          {/* Footer */}
          <div className="create-footer">
            {currentStep === 1 ? (
              <button className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            ) : (
              <button className="btn-secondary" onClick={handleBack}>
                Back
              </button>
            )}
            <button 
              className="btn-next" 
              onClick={currentStep === STEPS.length ? handleCreate : handleNext}
              disabled={
                (currentStep === 1 && (!formData.name.trim() || !formData.description.trim())) ||
                (currentStep === 2 && (banners.length === 0 || !formData.slogan.trim())) ||
                (currentStep === 3 && (
                  // At least one section must be enabled
                  (!contentConfig.linkVoucherEnabled && !contentConfig.linkCampaignEnabled) ||
                  // If voucher section is enabled, it must be fully configured
                  (contentConfig.linkVoucherEnabled && (!contentConfig.linkVoucherTitle.trim() || contentConfig.vouchers.length === 0)) ||
                  // If campaign section is enabled, it must be fully configured
                  (contentConfig.linkCampaignEnabled && (!contentConfig.linkCampaignTitle.trim() || !contentConfig.selectedCampaign))
                ))
              }
            >
              {currentStep === STEPS.length ? (isEditMode ? 'Save' : 'Create') : 'Next'}
            </button>
          </div>
        </div>
      </div>

      {/* Banner Modal */}
      {showBannerModal && (
        <div className="modal-overlay" onClick={closeBannerModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingBannerId ? 'Edit Banner' : 'Add Banner'}</h3>
              <button className="modal-close" onClick={closeBannerModal}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Banner Title</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  value={bannerForm.title}
                  onChange={handleBannerFormChange}
                  placeholder="Enter banner title"
                  maxLength={30}
                />
                <span className="char-count">{bannerForm.title.length}/30</span>
              </div>

              <div className="form-group">
                <label className="form-label">Banner Image</label>
                <div className="upload-area-wrapper">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                  />
                  {bannerForm.imageName ? (
                    <div 
                      className="upload-area banner-image-preview" 
                      onClick={() => {
                        // Skip cropping for GIF files to preserve animation
                        const isGif = bannerForm.imageName?.toLowerCase().endsWith('.gif');
                        if (isGif) {
                          toast.info('GIF files cannot be cropped (to preserve animation)');
                          return;
                        }
                        if (bannerForm.originalImageUrl) {
                          setCropImage(bannerForm.originalImageUrl);
                          setShowCropModal(true);
                          setIsReCropping(true); // This is re-cropping existing image
                          // Restore saved crop settings if available
                          setCrop(bannerForm.savedCrop || { x: 0, y: 0 });
                          setZoom(bannerForm.savedZoom || 1);
                        }
                      }}
                    >
                      <div className="banner-preview-content">
                        {(() => {
                          // Get image source: File object, base64, or server URL
                          const imageSrc = bannerImageThumbnail || bannerForm.originalImageUrl;
                          
                          return imageSrc ? (
                            <img 
                              src={imageSrc} 
                              alt={bannerForm.imageName} 
                              className="banner-thumbnail"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : null;
                        })()}
                        <span className="upload-filename">{bannerForm.imageName}</span>
                      </div>
                      <button
                        type="button"
                        className="upload-remove-text-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBannerForm(prev => ({
                            ...prev,
                            image: null,
                            imageName: '',
                            originalImageUrl: null
                          }));
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area" onClick={handleUploadClick}>
                      <IconUpload size={24} stroke={1.5} />
                      <span>Click to upload image</span>
                      <span className="upload-criteria">JPG, PNG, GIF or WEBP (Max 5MB, square ratio)</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Banner Link</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="linkType"
                      value="voucher"
                      checked={bannerForm.linkType === 'voucher'}
                      onChange={(e) => {
                        setBannerForm(prev => ({ ...prev, linkType: e.target.value, link: '' }));
                        setBannerLinkError(''); // Clear error when switching to voucher
                      }}
                    />
                    <span>Voucher Detail Page</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="linkType"
                      value="custom"
                      checked={bannerForm.linkType === 'custom'}
                      onChange={(e) => {
                        setBannerForm(prev => ({ ...prev, linkType: e.target.value, selectedVoucher: '' }));
                        setBannerLinkError(''); // Clear error when switching to custom
                      }}
                    />
                    <span>Custom Link</span>
                  </label>
                </div>

                {bannerForm.linkType === 'voucher' && (
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <div className="voucher-input-wrapper">
                      <div className="voucher-input-field">
                        <IconTicket size={20} stroke={1.5} className="voucher-input-icon" />
                        <span className={`voucher-input-text ${!bannerForm.selectedVoucher ? 'placeholder' : ''}`}>
                          {bannerForm.selectedVoucher 
                            ? apiVouchers.find(v => v.id === bannerForm.selectedVoucher)?.name || bannerForm.selectedVoucherData?.name || 'Selected voucher'
                            : 'Select a voucher'
                          }
                        </span>
                        {bannerForm.selectedVoucher && (
                          <button
                            type="button"
                            className="voucher-clear-btn"
                            onClick={() => setBannerForm(prev => ({ ...prev, selectedVoucher: '', selectedVoucherData: null }))}
                          >
                            <IconX size={16} stroke={2} />
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn-voucher-select"
                        onClick={() => {
                          setBannerVoucherSearch('');
                          setBannerVoucherFilters({ brand: '', category: '', voucherType: '', country: '' });
                          setTempSelectedVoucher(bannerForm.selectedVoucher || '');
                          setShowBannerVoucherModal(true);
                        }}
                      >
                        Select
                      </button>
                    </div>
                  </div>
                )}

                {bannerForm.linkType === 'custom' && (
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <input
                      type="url"
                      name="link"
                      className={`form-input ${bannerLinkError ? 'input-error' : ''}`}
                      value={bannerForm.link}
                      onChange={handleBannerFormChange}
                      placeholder="https://example.com"
                    />
                    {bannerLinkError && (
                      <div className="error-message" style={{ marginTop: '0.5rem', color: '#ef4444', fontSize: '0.85rem' }}>
                        {bannerLinkError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-next"
                onClick={handleBannerSubmit}
                disabled={
                  !bannerForm.title || 
                  !bannerForm.imageName || 
                  (bannerForm.linkType === 'voucher' && !bannerForm.selectedVoucher) ||
                  (bannerForm.linkType === 'custom' && (!bannerForm.link || bannerLinkError))
                }
              >
                {editingBannerId ? 'Confirm' : 'Add Banner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && cropImage && (
        <div className="modal-overlay" onClick={handleCropCancel}>
          <div className="modal-content modal-crop" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Crop Image</h3>
              <button className="modal-close" onClick={handleCropCancel}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body crop-modal-body">
              <div className="crop-container">
                <Cropper
                  image={cropImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="rect"
                  showGrid={true}
                />
              </div>
              <div className="crop-controls">
                <label className="crop-zoom-label">
                  Zoom:
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="crop-zoom-slider"
                  />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCropCancel}>
                Cancel
              </button>
              <button className="btn-next" onClick={handleCropConfirm}>
                Confirm Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Logo Crop Modal */}
      {showCompanyLogoCropModal && companyLogoCropImage && (
        <div className="modal-overlay">
          <div 
            className="modal-content modal-crop" 
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">Crop Company Logo</h3>
              <button className="modal-close" onClick={handleCompanyLogoCropCancel}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body crop-modal-body">
              <div 
                className="crop-container-logo"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <ReactCrop
                  crop={companyLogoCrop}
                  onChange={(c) => setCompanyLogoCrop(c)}
                  className="company-logo-crop"
                >
                  <img
                    ref={companyLogoImgRef}
                    src={companyLogoCropImage}
                    alt="Crop"
                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                  />
                </ReactCrop>
              </div>
              <p className="crop-hint">Drag the corners or edges to resize. Drag inside to move.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCompanyLogoCropCancel}>
                Cancel
              </button>
              <button className="btn-next" onClick={handleCompanyLogoCropConfirm}>
                Confirm Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (() => {
        const bannerToDelete = banners.find(b => b.id === deleteConfirmId);
        const bannerTitle = bannerToDelete?.title || 'this banner';
        return (
          <div className="modal-overlay" onClick={handleCancelDelete}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Delete Banner</h3>
                <button className="modal-close" onClick={handleCancelDelete}>
                  <IconX size={20} stroke={2} />
                </button>
              </div>
              <div className="modal-body">
                <p className="confirm-text">
                  Are you sure you want to delete <span className="highlight-text">{bannerTitle}</span>?
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={handleCancelDelete}>
                  Cancel
                </button>
                <button className="btn-danger" onClick={handleConfirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Voucher Confirmation Modal */}
      {deleteVoucherConfirmId && (() => {
        const voucherToDelete = contentConfig.vouchers.find(v => v.id === deleteVoucherConfirmId);
        const voucherName = voucherToDelete?.name || 'this voucher';
        return (
          <div className="modal-overlay" onClick={handleCancelVoucherDelete}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Delete Voucher</h3>
                <button className="modal-close" onClick={handleCancelVoucherDelete}>
                  <IconX size={20} stroke={2} />
                </button>
              </div>
              <div className="modal-body">
                <p className="confirm-text">
                  Are you sure you want to delete <span className="highlight-text">{voucherName}</span>?
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={handleCancelVoucherDelete}>
                  Cancel
                </button>
                <button className="btn-danger" onClick={handleConfirmVoucherDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Voucher Selection Modal (Content Configuration) */}
      {showVoucherModal && (
        <div className="modal-overlay" onClick={closeVoucherModal}>
          <div className="modal-content modal-voucher" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Select Voucher</h3>
              <button className="modal-close" onClick={closeVoucherModal}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body">
              {/* Filter Row */}
              <div className="voucher-filters">
                {/* Brand Filter */}
                {contentVoucherFilters.brand ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{contentVoucherFilters.brand}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setContentVoucherFilters(prev => ({ ...prev, brand: '' }))}
                      title="Clear brand filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setContentVoucherFilters(prev => ({ ...prev, brand: e.target.value }))}
                  >
                    <option value="">Brand</option>
                    {getFilterOptions(apiVouchers, 'brand').map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                )}
                
                {/* Category Filter */}
                {contentVoucherFilters.category ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{contentVoucherFilters.category}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setContentVoucherFilters(prev => ({ ...prev, category: '' }))}
                      title="Clear category filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setContentVoucherFilters(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="">Category</option>
                    {getFilterOptions(apiVouchers, 'category').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                )}
                
                {/* Voucher Type Filter */}
                {contentVoucherFilters.voucherType ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{contentVoucherFilters.voucherType}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setContentVoucherFilters(prev => ({ ...prev, voucherType: '' }))}
                      title="Clear voucher type filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setContentVoucherFilters(prev => ({ ...prev, voucherType: e.target.value }))}
                  >
                    <option value="">Voucher Type</option>
                    {getFilterOptions(apiVouchers, 'type').map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                )}
                
                {/* Country Filter */}
                {contentVoucherFilters.country ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{contentVoucherFilters.country}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setContentVoucherFilters(prev => ({ ...prev, country: '' }))}
                      title="Clear country filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setContentVoucherFilters(prev => ({ ...prev, country: e.target.value }))}
                  >
                    <option value="">Country</option>
                    {getFilterOptions(apiVouchers, 'country').map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                )}
                <div className="search-wrapper voucher-search-filter">
                  <IconSearch size={18} stroke={1.5} className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by voucher name"
                    value={contentVoucherSearch}
                    onChange={(e) => setContentVoucherSearch(e.target.value)}
                  />
                  {contentVoucherSearch && (
                    <button
                      className="search-clear-btn"
                      onClick={() => setContentVoucherSearch('')}
                      title="Clear search"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="voucher-select-table">
                <div className="voucher-select-header voucher-select-header-multi">
                  <span className="col-checkbox"></span>
                  <span>Voucher</span>
                  <span>Voucher Type</span>
                  <span>Price</span>
                  <span>Brand</span>
                  <span>Available Quantity</span>
                  <span>Validity Period</span>
                  <span>Category</span>
                  <span>Country</span>
                </div>
                {vouchersLoading ? (
                  <div className="voucher-loading">
                    <span>Loading vouchers...</span>
                  </div>
                ) : getContentFilteredVouchers().length === 0 ? (
                  <div className="voucher-empty">
                    <span>No vouchers found</span>
                  </div>
                ) : (
                  getContentFilteredVouchers().map((voucher) => {
                    const isAlreadyAdded = contentConfig.vouchers.some(v => v.id === voucher.id);
                    const isSelected = selectedVouchers.find(v => v.id === voucher.id);
                    return (
                      <div 
                        key={voucher.id} 
                        className={`voucher-select-row voucher-select-row-multi ${isSelected ? 'selected' : ''} ${isAlreadyAdded ? 'disabled' : ''}`}
                        onClick={() => !isAlreadyAdded && toggleVoucherSelection(voucher)}
                      >
                        <span className="col-checkbox">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            disabled={isAlreadyAdded}
                            onChange={() => !isAlreadyAdded && toggleVoucherSelection(voucher)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </span>
                        <span className="col-voucher">{voucher.name} {isAlreadyAdded && <span className="already-added-tag">(Added)</span>}</span>
                        <span>{voucher.type}</span>
                        <span>{voucher.price}</span>
                        <span>{voucher.brand}</span>
                        <span>{voucher.quantity}</span>
                        <span>{voucher.validPeriod}</span>
                        <span>{voucher.category}</span>
                        <span>{voucher.country}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div className="modal-footer voucher-modal-footer">
              <div className="voucher-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => loadVouchers(voucherPagination.page - 1, true, { search: contentVoucherSearch })}
                  disabled={voucherPagination.page <= 1 || vouchersLoading}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {voucherPagination.page} of {voucherPagination.lastPage}
                  {` · ${contentModalVoucherCount} voucher${contentModalVoucherCount === 1 ? '' : 's'}`}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => loadVouchers(voucherPagination.page + 1, true, { search: contentVoucherSearch })}
                  disabled={voucherPagination.page >= voucherPagination.lastPage || vouchersLoading}
                >
                  Next
                </button>
              </div>
              <div className="voucher-actions">
                <button 
                  className="btn-back"
                  onClick={closeVoucherModal}
                >
                  Cancel
                </button>
                <button 
                  className="btn-next"
                  onClick={handleAddSelectedVouchers}
                  disabled={selectedVouchers.length === 0}
                >
                  Add Voucher{selectedVouchers.length > 0 ? ` (${selectedVouchers.length})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner Voucher Selection Modal */}
      {showBannerVoucherModal && (
        <div className="modal-overlay" onClick={() => setShowBannerVoucherModal(false)}>
          <div className="modal-content modal-voucher" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Select Voucher</h3>
              <button className="modal-close" onClick={() => setShowBannerVoucherModal(false)}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body">
              {/* Filter Row */}
              <div className="voucher-filters">
                {/* Brand Filter */}
                {bannerVoucherFilters.brand ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{bannerVoucherFilters.brand}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setBannerVoucherFilters(prev => ({ ...prev, brand: '' }))}
                      title="Clear brand filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setBannerVoucherFilters(prev => ({ ...prev, brand: e.target.value }))}
                  >
                    <option value="">Brand</option>
                    {getFilterOptions(apiVouchers, 'brand').map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                )}
                
                {/* Category Filter */}
                {bannerVoucherFilters.category ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{bannerVoucherFilters.category}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setBannerVoucherFilters(prev => ({ ...prev, category: '' }))}
                      title="Clear category filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setBannerVoucherFilters(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="">Category</option>
                    {getFilterOptions(apiVouchers, 'category').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                )}
                
                {/* Voucher Type Filter */}
                {bannerVoucherFilters.voucherType ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{bannerVoucherFilters.voucherType}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setBannerVoucherFilters(prev => ({ ...prev, voucherType: '' }))}
                      title="Clear voucher type filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setBannerVoucherFilters(prev => ({ ...prev, voucherType: e.target.value }))}
                  >
                    <option value="">Voucher Type</option>
                    {getFilterOptions(apiVouchers, 'type').map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                )}
                
                {/* Country Filter */}
                {bannerVoucherFilters.country ? (
                  <div className="filter-chip">
                    <span className="filter-chip-value">{bannerVoucherFilters.country}</span>
                    <button
                      className="filter-chip-close"
                      onClick={() => setBannerVoucherFilters(prev => ({ ...prev, country: '' }))}
                      title="Clear country filter"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  </div>
                ) : (
                  <select
                    className="voucher-filter-select"
                    value=""
                    onChange={(e) => setBannerVoucherFilters(prev => ({ ...prev, country: e.target.value }))}
                  >
                    <option value="">Country</option>
                    {getFilterOptions(apiVouchers, 'country').map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                )}
                <div className="search-wrapper voucher-search-filter">
                  <IconSearch size={18} stroke={1.5} className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by voucher name"
                    value={bannerVoucherSearch}
                    onChange={(e) => setBannerVoucherSearch(e.target.value)}
                  />
                  {bannerVoucherSearch && (
                    <button
                      className="search-clear-btn"
                      onClick={() => setBannerVoucherSearch('')}
                      title="Clear search"
                    >
                      <IconX size={14} stroke={2} />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="voucher-select-table">
                <div className="voucher-select-header">
                  <span>Voucher</span>
                  <span>Voucher Type</span>
                  <span>Price</span>
                  <span>Brand</span>
                  <span>Available Quantity</span>
                  <span>Validity Period</span>
                  <span>Category</span>
                  <span>Country</span>
                </div>
                {vouchersLoading ? (
                  <div className="voucher-loading">
                    <span>Loading vouchers...</span>
                  </div>
                ) : getFilteredVouchers().length === 0 ? (
                  <div className="voucher-empty">
                    <span>No vouchers found</span>
                  </div>
                ) : (
                  getFilteredVouchers().map((voucher) => (
                    <div 
                      key={voucher.id} 
                      className={`voucher-select-row ${tempSelectedVoucher === voucher.id ? 'selected' : ''}`}
                      onClick={() => setTempSelectedVoucher(voucher.id)}
                    >
                      <span className="col-voucher">{voucher.name}</span>
                      <span>{voucher.type}</span>
                      <span>{voucher.price}</span>
                      <span>{voucher.brand}</span>
                      <span>{voucher.quantity}</span>
                      <span>{voucher.validPeriod}</span>
                      <span>{voucher.category}</span>
                      <span>{voucher.country}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="modal-footer voucher-modal-footer">
              <div className="voucher-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => loadVouchers(voucherPagination.page - 1, true, { search: bannerVoucherSearch })}
                  disabled={voucherPagination.page <= 1 || vouchersLoading}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {voucherPagination.page} of {voucherPagination.lastPage}
                  {` · ${bannerModalVoucherCount} voucher${bannerModalVoucherCount === 1 ? '' : 's'}`}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => loadVouchers(voucherPagination.page + 1, true, { search: bannerVoucherSearch })}
                  disabled={voucherPagination.page >= voucherPagination.lastPage || vouchersLoading}
                >
                  Next
                </button>
              </div>
              <div className="voucher-actions">
                <button 
                  className="btn-back"
                  onClick={() => setShowBannerVoucherModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-next"
                  onClick={() => {
                    const selectedVoucher = apiVouchers.find(v => v.id === tempSelectedVoucher);
                    setBannerForm(prev => ({
                      ...prev,
                      selectedVoucher: tempSelectedVoucher,
                      selectedVoucherData: selectedVoucher
                        ? serializeVoucherForPwaLink(selectedVoucher)
                        : (prev.selectedVoucher === tempSelectedVoucher ? prev.selectedVoucherData : null)
                    }));
                    setShowBannerVoucherModal(false);
                  }}
                  disabled={!tempSelectedVoucher}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Vouchers Modal */}
      {showCampaignVouchersModal && selectedCampaignForVouchers && (
        <div className="modal-overlay" onClick={() => setShowCampaignVouchersModal(false)}>
          <div className="modal-content modal-voucher" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Campaign Vouchers - {selectedCampaignForVouchers.name}</h3>
              <button className="modal-close" onClick={() => setShowCampaignVouchersModal(false)}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            {!campaignVouchersLoading && campaignVouchers.length > 0 && (
              <p className="campaign-vouchers-modal-count">
                {campaignVouchers.length} active voucher{campaignVouchers.length === 1 ? '' : 's'} (not expired)
              </p>
            )}
            <div className="modal-body">
              {campaignVouchersLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading vouchers...</div>
              ) : campaignVouchers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No vouchers found for this campaign.</div>
              ) : (
                <div className="voucher-select-table">
                  <div className="voucher-select-header campaign-vouchers-header">
                    <span>Voucher</span>
                    <span>Voucher Type</span>
                    <span>Price</span>
                    <span>Brand</span>
                    <span>Quantity</span>
                    <span>Valid Period</span>
                    <span>Category</span>
                    <span>Country</span>
                  </div>
                  {campaignVouchers.map((voucher) => (
                    <div key={voucher.id} className="voucher-select-row-multi campaign-vouchers-row">
                      <span>{voucher.name}</span>
                      <span>{voucher.type}</span>
                      <span>{voucher.price}</span>
                      <span>{voucher.brand}</span>
                      <span>{voucher.quantity}</span>
                      <span>{voucher.validPeriod}</span>
                      <span>{voucher.category}</span>
                      <span>{voucher.country}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCampaignVouchersModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateMarketingPage;
